import { useEffect, useMemo, useState } from 'react';
import { Search, DollarSign, Package, Truck, RotateCcw, RefreshCw, FileText, History, X as XIcon, MapPin, Store as StoreIcon, Receipt } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function authHeaders(): Record<string, string> {
  try {
    const raw = JSON.parse(localStorage.getItem('auth-storage') || '{}')?.state?.token || '';
    return raw ? { 'Content-Type': 'application/json', Authorization: 'Bearer ' + raw } : { 'Content-Type': 'application/json' };
  } catch { return { 'Content-Type': 'application/json' }; }
}

const fmtCRC = (n: number) => '₡' + Math.round(n).toLocaleString('es-CR');

type Modo = 'LOCAL' | 'MENSAJERO' | 'ENCOMIENDA';

interface Orden {
  id: string;
  codigoInterno: string;
  nombre: string;
  telefono: string;
  formato: string;
  cantidad: number;
  precioUnitario: number;
  monto: number;
  montoMontaje: number;
  montoFlete: number;
  montoEncomienda: number;
  montoPlanchados: number;
  montoTotal: number;
  lugar: string;
  modoEntrega: Modo;
  entregaDireccion: string;
  entregaReceptorNombre: string;
  entregaReceptorTel: string;
  estadoPago: 'PAGO_PENDIENTE' | 'CANCELADO';
  estadoEntrega: 'PENDIENTE' | 'ENTREGADO';
  requiereFactura: boolean;
  esCredito: boolean;
  nombreArchivo: string;
  comentario: string;
  fecha: string;
  createdAt: string;
  updatedAt: string;
  entregadoAt?: string;
  pagadoAt?: string;
}

interface ClienteQa { nombre: string; facturaElectronica: boolean; modoEntregaPreferido: string; direccionPreferida: string; }
interface AuditEntry { ts: string; userName: string; ordenId: string; accion: string; detalle: any; }

export default function CajaQA() {
  const [ordenes, setOrdenes] = useState<Orden[]>([]);
  const [clientes, setClientes] = useState<Record<string, ClienteQa>>({});
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [verEntregadas, setVerEntregadas] = useState(false);
  const [modoModal, setModoModal] = useState<Orden | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [toast, setToast] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/caja-qa/pendientes`, { headers: authHeaders() });
      const j = await r.json();
      if (j.success) { setOrdenes(j.data.ordenes || []); setClientes(j.data.clientes || {}); }
    } finally { setLoading(false); }
  };
  const loadAudit = async () => {
    const r = await fetch(`${API}/api/caja-qa/audit`, { headers: authHeaders() });
    const j = await r.json();
    if (j.success) setAuditLog(j.data || []);
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2200); };

  const resetQA = async () => {
    if (!confirm('¿Resetear datos QA a estado inicial? Se pierden todos los cambios de pruebas.')) return;
    const r = await fetch(`${API}/api/caja-qa/reset`, { method: 'POST', headers: authHeaders() });
    const j = await r.json();
    if (j.success) { showToast('✓ Datos QA restaurados'); load(); if (auditOpen) loadAudit(); }
  };

  const accion = async (o: Orden, endpoint: string, body?: any) => {
    const r = await fetch(`${API}/api/caja-qa/orden/${o.id}/${endpoint}`, {
      method: 'POST', headers: authHeaders(), body: body ? JSON.stringify(body) : undefined,
    });
    const j = await r.json();
    if (j.success) { showToast('✓ ' + endpoint); load(); if (auditOpen) loadAudit(); }
    else showToast('✗ ' + (j.error || 'error'));
    return j;
  };

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return ordenes.filter(o => {
      if (!verEntregadas && o.estadoEntrega === 'ENTREGADO') {
        // Mostrar las entregadas HOY si verEntregadas=false (para corregir error reciente)
        const hoy = new Date().toISOString().slice(0, 10);
        if ((o.entregadoAt || '').slice(0, 10) !== hoy) return false;
      }
      if (!q) return true;
      return o.codigoInterno?.toLowerCase().includes(q)
        || o.nombre?.toLowerCase().includes(q)
        || o.telefono?.includes(q)
        || o.formato?.toLowerCase().includes(q);
    });
  }, [ordenes, busqueda, verEntregadas]);

  const pendientes = ordenes.filter(o => o.estadoEntrega === 'PENDIENTE').length;
  const porCobrar = ordenes.filter(o => o.estadoPago === 'PAGO_PENDIENTE' && o.estadoEntrega === 'PENDIENTE').reduce((a, o) => a + o.montoTotal, 0);

  return (
    <div className="min-h-screen bg-gray-50 p-4 lg:p-6">
      {/* Banner QA */}
      <div className="mb-4 bg-amber-100 border-2 border-amber-400 rounded-xl px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-black text-amber-900 text-sm uppercase tracking-wide">⚠ Modo Q&A — Datos ficticios</div>
          <div className="text-xs text-amber-800">Nada afecta producción · Sheet · Clientes reales · Cuentas por Cobrar · Trabajos DTF</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setAuditOpen(!auditOpen); if (!auditOpen) loadAudit(); }}
            className="px-3 py-1.5 text-xs font-bold bg-white border border-amber-400 text-amber-800 rounded-lg hover:bg-amber-50 flex items-center gap-1.5">
            <History size={13}/> Audit log
          </button>
          <button onClick={resetQA}
            className="px-3 py-1.5 text-xs font-bold bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-1.5">
            <RefreshCw size={13}/> Reset QA
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-4 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">💰 Caja · Punto de Venta <span className="text-sm font-bold px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">Q&A</span></h1>
            <p className="text-xs text-gray-500 mt-0.5">{pendientes} pendientes · {fmtCRC(porCobrar)} por cobrar</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" checked={verEntregadas} onChange={e => setVerEntregadas(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600"/>
            Ver también entregadas (todas las fechas)
          </label>
        </div>

        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input
            type="text"
            placeholder="Buscar por código (OT-...), nombre, teléfono o formato..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400"
          />
        </div>
      </div>

      {/* Lista */}
      <div className="space-y-3">
        {loading && <div className="text-center py-10 text-sm text-gray-400">Cargando…</div>}
        {!loading && filtradas.length === 0 && (
          <div className="text-center py-10 text-sm text-gray-400 bg-white border border-gray-200 rounded-2xl">
            {busqueda ? 'Sin resultados' : 'No hay órdenes pendientes'}
          </div>
        )}
        {filtradas.map(o => <OrdenCard key={o.id} orden={o} cliente={clientes[o.telefono]} onAccion={accion} onCambiarModo={() => setModoModal(o)} />)}
      </div>

      {/* Modal cambiar modo */}
      {modoModal && (
        <ModoModal
          orden={modoModal}
          cliente={clientes[modoModal.telefono]}
          onClose={() => setModoModal(null)}
          onGuardar={async (payload) => { await accion(modoModal, 'modo-entrega', payload); setModoModal(null); }}
        />
      )}

      {/* Audit log sidebar */}
      {auditOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 flex justify-end" onClick={() => setAuditOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2"><History size={16}/> Audit log Q&A</h3>
              <button onClick={() => setAuditOpen(false)}><XIcon size={20}/></button>
            </div>
            <div className="p-4 space-y-2">
              {auditLog.length === 0 && <div className="text-xs text-gray-400 text-center py-6">Sin acciones registradas</div>}
              {auditLog.map((e, i) => (
                <div key={i} className="text-xs border border-gray-100 rounded-lg p-2.5 bg-gray-50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-indigo-700">{e.accion}</span>
                    <span className="text-gray-400">{new Date(e.ts).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                  </div>
                  <div className="text-gray-600"><span className="font-mono">{e.ordenId}</span> por <strong>{e.userName}</strong></div>
                  {e.detalle && <pre className="mt-1 text-[10px] text-gray-500 overflow-x-auto">{JSON.stringify(e.detalle)}</pre>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 right-5 bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

// ─── Card de orden ─────────────────────────────────────────────
function OrdenCard({ orden, cliente, onAccion, onCambiarModo }: { orden: Orden; cliente?: ClienteQa; onAccion: (o: Orden, ep: string, body?: any) => Promise<any>; onCambiarModo: () => void }) {
  const modoColor: Record<Modo, string> = {
    LOCAL: 'bg-blue-100 text-blue-700',
    MENSAJERO: 'bg-amber-100 text-amber-800',
    ENCOMIENDA: 'bg-purple-100 text-purple-700',
  };
  const modoIcon: Record<Modo, any> = { LOCAL: StoreIcon, MENSAJERO: Truck, ENCOMIENDA: Package };
  const Icon = modoIcon[orden.modoEntrega];
  const yaEntregado = orden.estadoEntrega === 'ENTREGADO';
  const yaPagado = orden.estadoPago === 'CANCELADO';
  const hoyKey = new Date().toISOString().slice(0, 10);
  const puedeRevertir = yaEntregado && (orden.entregadoAt || '').slice(0, 10) === hoyKey;

  return (
    <div className={`bg-white border rounded-xl p-4 shadow-sm ${yaEntregado ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono font-bold bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">{orden.codigoInterno}</span>
          <h3 className={`font-bold text-gray-900 ${yaEntregado ? 'line-through text-gray-500' : ''}`}>{orden.nombre}</h3>
          <span className="text-sm text-gray-400 font-mono">{orden.telefono}</span>
          {orden.esCredito && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">Crédito</span>}
          {orden.requiereFactura && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">Factura ✓</span>}
          {yaPagado && yaEntregado && <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-bold">✓ Cerrada</span>}
          {!yaPagado && yaEntregado && <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full font-bold">Entregada · pago pendiente</span>}
        </div>
        <div className="text-right">
          <div className="text-lg font-black text-gray-900 tabular-nums">{fmtCRC(orden.montoTotal)}</div>
          <div className="text-[10px] text-gray-400">{orden.fecha}</div>
        </div>
      </div>

      <div className="flex items-center gap-3 text-sm text-gray-700 mb-3 flex-wrap">
        <span className="font-semibold">{orden.formato}</span>
        <span>×{orden.cantidad}</span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${modoColor[orden.modoEntrega]}`}>
          <Icon size={11}/> {orden.modoEntrega === 'LOCAL' ? 'Tienda' : orden.modoEntrega === 'MENSAJERO' ? 'Mensajero' : 'Encomienda'}
        </span>
        {orden.entregaDireccion && <span className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={11}/>{orden.entregaDireccion}</span>}
      </div>

      {orden.comentario && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1.5 rounded mb-3">💬 {orden.comentario}</div>
      )}

      {/* Acciones */}
      {!yaEntregado && (
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => onAccion(orden, 'cobrar')}
            className="flex-1 min-w-[140px] px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-1.5">
            <DollarSign size={14}/> Cobrar y entregar
          </button>
          <button onClick={() => onAccion(orden, 'entregar')}
            className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold flex items-center gap-1.5">
            <Package size={14}/> Entregar sin cobrar
          </button>
          <button onClick={onCambiarModo}
            className="px-3 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg text-sm font-bold flex items-center gap-1.5">
            <Truck size={14}/> Cambiar modo
          </button>
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer ml-auto">
            <input type="checkbox" checked={orden.requiereFactura}
              onChange={e => onAccion(orden, 'factura', { requiereFactura: e.target.checked, propagarACliente: true })}
              className="w-4 h-4 rounded border-gray-300 text-purple-600"/>
            <Receipt size={13}/> Factura electrónica
          </label>
        </div>
      )}

      {yaEntregado && puedeRevertir && (
        <button onClick={() => onAccion(orden, 'revertir')}
          className="text-xs text-gray-600 hover:text-red-600 flex items-center gap-1">
          <RotateCcw size={11}/> Revertir (entregada hoy)
        </button>
      )}

      {cliente && (
        <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-400 flex items-center gap-3">
          <span>Preferencia cliente:</span>
          {cliente.facturaElectronica && <span className="text-purple-600">Factura ✓</span>}
          <span>Modo: {cliente.modoEntregaPreferido}</span>
          {cliente.direccionPreferida && <span>· {cliente.direccionPreferida}</span>}
        </div>
      )}
    </div>
  );
}

// ─── Modal Cambiar modo de entrega ─────────────────────────────
function ModoModal({ orden, cliente, onClose, onGuardar }: { orden: Orden; cliente?: ClienteQa; onClose: () => void; onGuardar: (p: any) => Promise<void> }) {
  const [modo, setModo] = useState<Modo>(orden.modoEntrega);
  const [direccion, setDireccion] = useState(orden.entregaDireccion || '');
  const [receptorNombre, setReceptorNombre] = useState(orden.entregaReceptorNombre || '');
  const [receptorTel, setReceptorTel] = useState(orden.entregaReceptorTel || '');
  const [lugar, setLugar] = useState(orden.lugar || '');
  const [montoFlete, setMontoFlete] = useState(String(orden.montoFlete || 0));
  const [montoEncomienda, setMontoEncomienda] = useState(String(orden.montoEncomienda || 0));
  const [propagarACliente, setPropagarACliente] = useState(false);
  const [saving, setSaving] = useState(false);

  const nuevoFlete = modo === 'MENSAJERO' ? parseFloat(montoFlete) || 0 : 0;
  const nuevoEncomienda = modo === 'ENCOMIENDA' ? parseFloat(montoEncomienda) || 0 : 0;
  const nuevoTotal = orden.monto + (orden.montoMontaje || 0) + nuevoFlete + nuevoEncomienda + (orden.montoPlanchados || 0);
  const diferencia = nuevoTotal - orden.montoTotal;
  const yaPagado = orden.estadoPago === 'CANCELADO';

  const guardar = async () => {
    setSaving(true);
    try {
      await onGuardar({ modo, direccion, receptorNombre, receptorTel, lugar, montoFlete: nuevoFlete, montoEncomienda: nuevoEncomienda, propagarACliente });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Truck size={18}/> Cambiar modo de entrega</h3>
          <button onClick={onClose}><XIcon size={20}/></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 block">Modo</label>
            <div className="grid grid-cols-3 gap-2">
              {(['LOCAL', 'MENSAJERO', 'ENCOMIENDA'] as Modo[]).map(m => (
                <button key={m} onClick={() => setModo(m)}
                  className={`py-3 rounded-lg text-sm font-bold border-2 transition-colors ${modo === m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'}`}>
                  {m === 'LOCAL' ? '🏪 Tienda' : m === 'MENSAJERO' ? '🚚 Mensajero' : '📦 Encomienda'}
                </button>
              ))}
            </div>
          </div>

          {modo === 'LOCAL' && (
            <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-3">Cliente recoge en tienda. Sin monto adicional.</div>
          )}

          {(modo === 'MENSAJERO' || modo === 'ENCOMIENDA') && (
            <>
              <div>
                <label className="text-xs font-bold text-gray-700 uppercase mb-1 block">Dirección / Destino</label>
                <input value={direccion} onChange={e => setDireccion(e.target.value)}
                  placeholder={modo === 'MENSAJERO' ? 'Ej. Escazú Centro, frente a Multiplaza' : 'Ej. Terminal Puntarenas'}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase mb-1 block">Recibe</label>
                  <input value={receptorNombre} onChange={e => setReceptorNombre(e.target.value)}
                    placeholder="Nombre"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"/>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase mb-1 block">Teléfono</label>
                  <input value={receptorTel} onChange={e => setReceptorTel(e.target.value)}
                    placeholder="88887777"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"/>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-700 uppercase mb-1 block">
                  Monto {modo === 'MENSAJERO' ? 'flete mensajero' : 'encomienda'}
                </label>
                <input type="number"
                  value={modo === 'MENSAJERO' ? montoFlete : montoEncomienda}
                  onChange={e => modo === 'MENSAJERO' ? setMontoFlete(e.target.value) : setMontoEncomienda(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-right tabular-nums font-bold focus:outline-none focus:border-indigo-400"/>
              </div>
            </>
          )}

          {/* Comparativa de monto */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Monto anterior:</span><span className="tabular-nums">{fmtCRC(orden.montoTotal)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Monto nuevo:</span><span className="tabular-nums font-bold text-indigo-700">{fmtCRC(nuevoTotal)}</span></div>
            {diferencia !== 0 && (
              <div className={`flex justify-between pt-1.5 border-t border-gray-200 font-bold ${diferencia > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                <span>{yaPagado ? (diferencia > 0 ? 'Cliente debe pagar extra:' : 'Devolver al cliente:') : (diferencia > 0 ? 'A cobrar extra:' : 'Menor que lo pactado:')}</span>
                <span className="tabular-nums">{fmtCRC(Math.abs(diferencia))}</span>
              </div>
            )}
          </div>

          {cliente && (
            <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer bg-indigo-50/50 border border-indigo-200 rounded-lg p-2.5">
              <input type="checkbox" checked={propagarACliente} onChange={e => setPropagarACliente(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded border-gray-300 text-indigo-600 flex-shrink-0"/>
              <span>Guardar como <strong>preferencia por defecto</strong> del cliente ({cliente.nombre}). La próxima orden ya aparecerá con este modo/dirección pre-cargado.</span>
            </label>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-white">Cancelar</button>
          <button onClick={guardar} disabled={saving}
            className="px-5 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}
